import {
  Component,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Self,
  SimpleChanges,
  inject,
} from '@angular/core';
import { NgControl, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import {
  ControlsOf,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
} from '@ngneat/reactive-forms';
import { Observable } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

import {
  Generator,
  GeneratorFormElements,
  GeneratorInternalFormElements,
  GeneratorModel,
} from '@digital-platform/generators/domain';
import {
  AddressInputFields,
  AddressModel,
  ContactModel,
} from '@digital-platform/shared/domain';
import {
  FormValidators,
  NestedFormComponent,
} from '@digital-platform/shared/ui';
import {
  CompanyNameService,
  LoginSessionFacadeService,
} from '@digital-platform/users/services';
import {
  InternalStatus,
  WasteProfileCustomerCard,
  WasteProfileStatus,
} from '@digital-platform/waste-profiles/domain';
import {
  WasteProfileCardsService,
  WasteProfilesFacadeService,
} from '@digital-platform/waste-profiles/services';

type GeneratorInternalFormControls = ControlsOf<
  Omit<
    GeneratorInternalFormElements,
    'siteAddress' | 'generatorAddress' | 'thirdPartyControl'
  >
> & {
  generatorAddress: FormControl<AddressInputFields>;
  siteAddress: FormControl<AddressInputFields>;
};

@Component({
  selector: 'digital-platform-add-generator-form',
  templateUrl: './add-generator-form.component.html',
  styleUrls: ['./add-generator-form.component.scss'],
})
export class AddGeneratorFormComponent
  extends NestedFormComponent<
    GeneratorFormElements,
    GeneratorInternalFormControls
  >
  implements OnInit, ControlValueAccessor<GeneratorFormElements>, OnChanges
{
  public form: FormGroup<GeneratorInternalFormControls>;
  @Input() displayMode: 'customer' | 'admin';
  @Input() profileStatus: string;
  @Input() isLegacy = false;
  @Input() tabIndex = 1;

  isRequired = true;
  hasPermission: boolean;
  isDisabled = false;
  isGeneratorDisabled$: Observable<boolean>;
  waitForProfiles$: Observable<WasteProfileCustomerCard[]>;
  generatorFieldsDisabled: boolean;

  filteredCustomers: Observable<string[]> | undefined;

  customerNames: string[] = [];

  private wasteProfileCardsService = inject(WasteProfileCardsService);

  constructor(
    private formBuilder: FormBuilder,
    private readonly facade: LoginSessionFacadeService,
    private readonly wasteProfileFacadeService: WasteProfilesFacadeService,
    private readonly companyNameService: CompanyNameService,
    @Optional() @Self() public controlDir: NgControl
  ) {
    super(controlDir);
    this.waitForProfiles$ = this.wasteProfileCardsService.statusCards$;
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.profileStatus?.currentValue === 'Not Submitted') {
      this.form.controls.name.removeValidators(Validators.required);
      this.form.controls.parentCustomerName.removeValidators(
        Validators.required
      );
      this.form.controls.customerName.removeValidators(Validators.required);
      this.form.controls.regulatoryIdNumber.removeValidators(
        Validators.required
      );
      this.form.controls.contact.controls.name.removeValidators(
        Validators.required
      );
      this.form.controls.contact.controls.phone.removeValidators(
        Validators.required
      );
      this.form.controls.contact.controls.email.removeValidators(
        Validators.required
      );
      this.isRequired = false;
    } else if (
      changes.profileStatus?.currentValue &&
      changes.profileStatus?.currentValue !== 'Not Submitted' &&
      this.isLegacy === false
    ) {
      this.form.controls.name.addValidators(Validators.required);
      this.form.controls.regulatoryIdNumber.addValidators(Validators.required);

      this.form.controls.parentCustomerName.addValidators(Validators.required);
      this.form.controls.customerName.addValidators(Validators.required);
      this.form.controls.contact.controls.name.addValidators(
        Validators.required
      );
      this.form.controls.contact.controls.phone.addValidators(
        Validators.required
      );
      this.form.controls.contact.controls.email.addValidators(
        Validators.required
      );
      this.onStateChange();
      this.isRequired = true;
    }
  }

  initializeForm(generatorForm) {
    this.form = this.formBuilder.group({
      isMailingAddressSameAsSite: [generatorForm?.isMailingAddressSameAsSite],
      isOneTimeOnly: [generatorForm?.isOneTimeOnly],
      regulatoryIdNumberNotApplicable: [
        generatorForm?.regulatoryIdNumberNotApplicable,
      ],
      parentCustomerName:
        this.isLegacy === false
          ? [
              generatorForm?.parentCustomerName,
              [
                FormValidators.requiredIf(
                  () =>
                    this.hasPermission && this.profileStatus !== 'Not Submitted'
                ),
              ],
            ]
          : [generatorForm?.parentCustomerName],
      customerName:
        this.isLegacy === false
          ? [
              generatorForm?.customerName,
              [
                FormValidators.requiredIf(
                  () =>
                    this.hasPermission && this.profileStatus !== 'Not Submitted'
                ),
              ],
            ]
          : [generatorForm?.customerName],
      name:
        this.isLegacy === false
          ? [generatorForm?.name, [Validators.required]]
          : [generatorForm?.name],
      naicsNumber: [generatorForm?.naicsNumber],
      regulatoryIdNumber:
        this.isLegacy === false
          ? [generatorForm?.regulatoryIdNumber, [Validators.required]]
          : [generatorForm?.regulatoryIdNumber],
      epaId: [generatorForm?.epaId],
      generatorAddress: this.formBuilder.control<AddressInputFields>({
        addressLine1: generatorForm?.generatorAddress?.addressLine1,
        addressLine2: generatorForm?.generatorAddress?.addressLine2,
        addressLine3: generatorForm?.generatorAddress?.addressLine3,
        city: generatorForm?.generatorAddress?.city,
        county: generatorForm?.generatorAddress?.county,
        stateCode: generatorForm?.generatorAddress?.stateCode,
        postalCode: generatorForm?.generatorAddress?.postalCode,
        postalCodeSuffix: generatorForm?.generatorAddress?.postalCodeSuffix,
        country: generatorForm?.generatorAddress?.country || 'US',
      }),
      siteAddress: this.formBuilder.control<AddressInputFields>({
        addressLine1: generatorForm?.siteAddress?.addressLine1,
        addressLine2: generatorForm?.siteAddress?.addressLine2,
        addressLine3: generatorForm?.siteAddress?.addressLine3,
        city: generatorForm?.siteAddress?.city,
        county: generatorForm?.siteAddress?.county,
        stateCode: generatorForm?.siteAddress?.stateCode,
        postalCode: generatorForm?.siteAddress?.postalCode,
        postalCodeSuffix: generatorForm?.siteAddress?.postalCodeSuffix,
        country: generatorForm?.siteAddress?.country || 'US',
      }),

      contact: this.formBuilder.group({
        name:
          this.isLegacy === false
            ? [generatorForm.contact?.name, [Validators.required]]
            : [generatorForm.contact?.name],
        phone:
          this.isLegacy === false
            ? [
                generatorForm.contact?.phone,
                [
                  Validators.required,
                  Validators.pattern(
                    /^[(]{0,1}[0-9]{3}[)]{0,1}[-\s.]{0,1}[0-9]{3}[-\s.]{0,1}[0-9]{4}/
                  ),
                ],
              ]
            : [generatorForm.contact?.phone],
        phoneExt: [generatorForm.contact?.phoneExt],
        email:
          this.isLegacy === false
            ? [
                generatorForm.contact?.email,
                [Validators.required, Validators.email],
              ]
            : [generatorForm.contact?.email],
        fax: [generatorForm.contact?.fax],
      }),
      thirdPartyControl: this.formBuilder.control({
        isCustomer3rdParty: generatorForm?.isCustomer3rdParty,
        is3rdPartyAuthorized: generatorForm?.is3rdPartyAuthorized,
      }),
    });

    if (this.hasPermission) {
      this.companyNameService.getCompanyNameList();

      this.companyNameService.companyNameList$
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response) => {
          this.customerNames = response;
        });
    }

    this.filteredCustomers = this.form.controls.customerName.valueChanges.pipe(
      startWith(''),
      map((value) =>
        this.companyNameService._filter(this.customerNames, value || '')
      )
    );
  }

  async ngOnInit(): Promise<void> {
    let generatorStream: GeneratorFormElements = {
      ...GeneratorModel.DEFAULT_CONFIG,
      generatorAddress: {
        ...AddressModel.DEFAULT_CONFIG,
        addressLine2: '',
        addressLine3: '',
        city: '',
        stateCode: '',
        county: '',
        postalCodeSuffix: '',
        country: '',
      },
      siteAddress: {
        ...AddressModel.DEFAULT_CONFIG,
        addressLine2: '',
        addressLine3: '',
        city: '',
        stateCode: '',
        county: '',
        postalCodeSuffix: '',
        country: '',
      },
      contact: {
        ...ContactModel.DEFAULT_CONFIG,
        email: '',
        phoneExt: '',
        fax: '',
      },
    };

    if (this.value) {
      generatorStream = this.value;
    }

    this.isGeneratorDisabled$ = this.waitForProfiles$.pipe(
      map((wasteProfiles) =>
        wasteProfiles.some((f) => {
          const siteAddress1 = {
            addressLine1: this.value.siteAddress?.addressLine1,
            addressLine2: this.value.siteAddress?.addressLine2,
            addressLine3: this.value.siteAddress?.addressLine3,
            city: this.value.siteAddress?.city,
            country: this.value.siteAddress?.country,
            county: this.value.siteAddress?.county,
            postalCode: this.value.siteAddress?.postalCode,
            postalCodeSuffix: this.value.siteAddress?.postalCodeSuffix,
            stateCode: this.value.siteAddress?.stateCode,
          };

          const siteAddress2 = {
            addressLine1: f.generator?.siteAddress?.addressLine1,
            addressLine2: f.generator?.siteAddress?.addressLine2,
            addressLine3: f.generator?.siteAddress?.addressLine3,
            city: f.generator?.siteAddress?.city,
            country: f.generator?.siteAddress?.country,
            county: f.generator?.siteAddress?.county,
            postalCode: f.generator?.siteAddress?.postalCode,
            postalCodeSuffix: f.generator?.siteAddress?.postalCodeSuffix,
            stateCode: f.generator?.siteAddress?.stateCode,
          };

          const val =
            f.generator?.name === this.value?.name &&
            JSON.stringify(siteAddress1) === JSON.stringify(siteAddress2) &&
            (f.status === WasteProfileStatus.APPROVED ||
              f.status === WasteProfileStatus.EXPIRING ||
              f.internalStatus === InternalStatus.ANALYST_APPROVED);
          return val;
        })
      )
    );

    if (this.form) {
      this.form.patchValue(generatorStream, {
        emitEvent: false,
      });
    } else {
      this.hasPermission = await this.facade.hasPermission(
        'update:generator:parent_customer'
      );
      this.initializeForm(generatorStream);
      this.onStateChange();
    }
    this.onStateIdChange();
    this.isGeneratorDisabled$.subscribe((f) => {
      this.generatorFieldsDisabled = f;
    });
    super.ngOnInit();
  }

  errorHandling = (control: string, error: string) => {
    if (
      this.form.controls[control].touched &&
      this.form.controls[control].hasError(error)
    ) {
      this.form.updateValueAndValidity();
      return true;
    }
    return false;
  };

  errorHandlingContact = (control: string, error: string) => {
    if (
      this.form.controls.contact.controls[control].touched &&
      this.form.controls.contact.controls[control].hasError(error)
    ) {
      this.form.updateValueAndValidity();
      return true;
    }
    return false;
  };

  onStateChange = () => {
    const stateApprovalWasteCodeNotApplicableControl = this.form.get(
      'regulatoryIdNumberNotApplicable'
    );
    const regulatoryIdNumberControl = this.form.get('regulatoryIdNumber');

    const updateValidationState = (
      val: Generator['regulatoryIdNumberNotApplicable']
    ) => {
      if (val === true) {
        regulatoryIdNumberControl.setValue(null);
        regulatoryIdNumberControl.clearValidators();
      } else {
        regulatoryIdNumberControl.setValidators([Validators.required]);
      }

      regulatoryIdNumberControl.updateValueAndValidity();
    };

    updateValidationState(stateApprovalWasteCodeNotApplicableControl.value);
    stateApprovalWasteCodeNotApplicableControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(updateValidationState);
  };

  onSameAddressChange = ($event: MatCheckboxChange) => {
    const generatorAddressGroup = this.form.get('generatorAddress');
    const siteAddressGroup = this.form.get('siteAddress');
    if ($event.checked) {
      generatorAddressGroup.patchValue(siteAddressGroup.value);
      generatorAddressGroup.disable();
      this.isDisabled = true;
    } else {
      generatorAddressGroup.enable();
      this.isDisabled = false;
      generatorAddressGroup.reset();
      generatorAddressGroup.patchValue({ country: 'US' } as AddressInputFields);
      generatorAddressGroup.updateValueAndValidity();
    }
  };

  public mapOutputValue(
    value: GeneratorInternalFormElements
  ): GeneratorFormElements {
    const { thirdPartyControl, ...generatorInfo } = value;
    let updatedGeneratorInfo = generatorInfo;

    if (generatorInfo.isMailingAddressSameAsSite) {
      updatedGeneratorInfo = {
        ...generatorInfo,
        generatorAddress: {
          ...generatorInfo.siteAddress,
        },
      };
    }

    return {
      ...updatedGeneratorInfo,
      ...thirdPartyControl,
    };
  }

  onStateIdChange = () => {
    const stateApprovalWasteCodeNotApplicableControl = this.form.get(
      'regulatoryIdNumberNotApplicable'
    );
    const regulatoryIdNumberControl = this.form.get('regulatoryIdNumber');

    const updateValidationState = (val: Generator['regulatoryIdNumber']) => {
      if (val) {
        stateApprovalWasteCodeNotApplicableControl.setValue(false, {
          emitEvent: false,
        });
      }
    };

    updateValidationState(regulatoryIdNumberControl.value);
    regulatoryIdNumberControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(updateValidationState);
  };
}
